import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, BadgeCheck, MonitorSmartphone } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enterPortal } from "@/lib/mode";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/config").then(setConfig).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const openPortal = () => {
    enterPortal();
    navigate("/portal", { replace: true });
  };

  return (
    <div className="min-h-screen p-6 md:p-10">
      <header className="mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-soma-brand">// your account</p>
        <h1 className="mt-1 font-display text-4xl font-medium text-soma-on">Profile</h1>
      </header>

      <div className="max-w-xl space-y-6">
        <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-soma-brand/15 font-display text-2xl text-soma-brand">
              {user?.full_name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <p className="text-lg font-medium text-soma-on">{user?.full_name}</p>
              <p className="text-sm text-soma-muted">{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-6">
          <div className="flex items-center gap-2">
            <BadgeCheck size={18} className="text-soma-brand" />
            <h3 className="font-display text-xl text-soma-on">Subscription plan</h3>
          </div>
          <p className="mt-3 text-sm text-soma-on2">
            Flat monthly access. There&apos;s no in-app payment — every reading you give is free
            within the app, and billing stays off-platform.
          </p>
        </div>

        {config && (config.soma_lumin_url || config.somalixir_url) && (
          <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-6">
            <h3 className="mb-3 font-display text-xl text-soma-on">The Soma family</h3>
            <div className="space-y-2">
              {config.soma_lumin_url && (
                <a href={config.soma_lumin_url} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-xl bg-soma-surface3 px-4 py-3 text-sm text-soma-on2 hover:text-soma-brand">
                  Soma Lumin · Aura readings <ExternalLink size={15} />
                </a>
              )}
              {config.somalixir_url && (
                <a href={config.somalixir_url} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-xl bg-soma-surface3 px-4 py-3 text-sm text-soma-on2 hover:text-soma-brand">
                  Somalixir · Tasseography <ExternalLink size={15} />
                </a>
              )}
            </div>
          </div>
        )}

        <button
          onClick={openPortal}
          data-testid="open-portal-button"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-soma-brand py-3.5 text-sm font-semibold text-soma-surface transition-opacity hover:opacity-90"
        >
          <MonitorSmartphone size={18} /> Open the Palm Portal
        </button>

        <button
          onClick={handleLogout}
          data-testid="profile-logout-button"
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-soma-border bg-soma-surface2 py-3.5 text-sm font-medium text-soma-on2 transition-colors hover:border-soma-error hover:text-soma-on"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );
}
